import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik } from 'formik';

import LabeledTextInput from 'components/consumer/LabeledTextInput';
import SmartyStreetsFloatingField from 'components/essentials/SmartyStreetsFloatingField';
import FormikFirstErrorFieldFocusOnSubmit from 'components/essentials/FormikFirstErrorFieldFocusOnSubmit';
import { ZIP_CODE_REGEX, EMAIL_REGEX } from 'commons/constants';
import ToggleSwitch from 'components/consumer/ToggleSwitch';
import TwoColumnFormFields from 'components/consumer/TwoColumnFormFields';
import Link from 'components/consumer/Link';
import ZipField from './ZipField';
import CityField from './CityField';
import StateField from './StateField';
import TradeAddressTypeField from './TradeAddressTypeField';
import ApartmentField from './ApartmentField';
import PhoneNumberField from './PhoneNumberField';
import ShippingEmailField from './ShippingEmailField';
import FirstNameField from './FirstNameField';
import LastNameField from './LastNameField';
import CheckoutSubmitButton from '../../CheckoutSubmitButton';

const INITIAL_VALUES = {
  first_name: '',
  last_name: '',
  address: '',
  city: '',
  apartment: '',
  company: '',
  state: '',
  zipcode: '',
  phone_number: '',
  shipping_email: '',
  shipping_sms: false,
  commercial_address: '0',
};

const shippingFormSchemaFields = {
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string().required('Last name is required'),
  address: Yup.string()
    .required('Street address is required')
    .min(6, 'Street Address must contain minimum six characters'),
  city: Yup.string().required('City is required'),
  apartment: Yup.string(),
  company: Yup.string(),
  state: Yup.string().required('State is required'),
  zipcode: Yup.string()
    .required('Zip code is required')
    .matches(ZIP_CODE_REGEX, 'Invalid zip code'),
  phone_number: Yup.string()
    .required('Please enter a valid phone number')
    .length(14, 'Please enter a valid phone number'),
};

const tradeShippingFormSchemaFields = {
  ...shippingFormSchemaFields,
  shipping_email: Yup.string().matches(
    EMAIL_REGEX,
    'Invalid shipping email address'
  ),
};

export const shippingFormSchema = Yup.object().shape(shippingFormSchemaFields);

export const tradeShippingFormSchema = Yup.object().shape(
  tradeShippingFormSchemaFields
);

const Shipping = props => {
  const initialValues = props.shipping || INITIAL_VALUES;
  const formRef = useRef(null);
  const addressRef = useRef();
  const formSchema = props.isTradeUser
    ? tradeShippingFormSchema
    : shippingFormSchema;
  // validate effect
  useEffect(() => {
    if (props.validateFormOnLoad && formRef.current?.submitForm) {
      formRef.current.submitForm();
    }
  }, [props.validateFormOnLoad]);

  return (
    <Formik
      ref={formRef}
      initialValues={initialValues}
      isInitialValid={formSchema.isValidSync(initialValues)}
      validationSchema={formSchema}
      onSubmit={props.onSubmit}
      render={({
        isValid,
        values,
        errors,
        touched,
        submitCount,
        handleSubmit,
        handleBlur,
        handleChange,
        setFieldValue,
        setFieldTouched,
      }) => (
        <form
          onSubmit={handleSubmit}
          data-xc="shipping-form"
          className="shipping"
        >
          <div className="mt-0 md:mt-6">
            <p className="text-sm text-gray-light1">
              Fields marked with an asterisk (*) are required.
            </p>

            <TwoColumnFormFields>
              <FirstNameField
                autoFocus
                handleBlur={handleBlur}
                error={errors.first_name}
                value={values.first_name}
                handleChange={handleChange}
                isTouched={touched.first_name}
              />

              <LastNameField
                handleBlur={handleBlur}
                error={errors.last_name}
                value={values.last_name}
                handleChange={handleChange}
                isTouched={touched.last_name}
              />
            </TwoColumnFormFields>

            <SmartyStreetsFloatingField
              showError
              refField={addressRef}
              error={errors.address}
              value={values.address}
              handleBlur={handleBlur}
              handleChange={handleChange}
              isTouched={touched.address}
              setFieldValue={setFieldValue}
              updateShipping={newVal => {
                props.updateShipping({ ...values, ...newVal });
              }}
            />

            <CityField
              value={values.city}
              error={errors.city}
              isTouched={touched.city}
              handleBlur={handleBlur}
              handleChange={handleChange}
              updateShipping={() => {
                props.updateShipping(values);
              }}
            />

            <TwoColumnFormFields>
              <ApartmentField
                value={values.apartment}
                error={errors.apartment}
                handleBlur={handleBlur}
                handleChange={handleChange}
                isTouched={touched.apartment}
              />

              <LabeledTextInput
                id="company"
                type="text"
                name="company"
                label="Company (optional)"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.company}
                autoComplete="organization"
                data-xc="shipping-company-field"
              />
            </TwoColumnFormFields>

            <TwoColumnFormFields className="lg:!items-stretch">
              <StateField
                value={values.state}
                error={errors.state}
                handleBlur={handleBlur}
                isTouched={touched.state}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                updateShipping={newVal => {
                  props.updateShipping({ ...values, ...newVal });
                }}
              />

              <ZipField
                value={values.zipcode}
                error={errors.zipcode}
                handleBlur={handleBlur}
                isTouched={touched.zipcode}
                handleChange={handleChange}
                updateShipping={() => {
                  props.updateShipping(values);
                }}
              />
            </TwoColumnFormFields>

            {props.isTradeUser && (
              <TradeAddressTypeField
                value={values.commercial_address}
                error={errors.commercial_address}
                handleBlur={handleBlur}
                isTouched={touched.commercial_address}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                updateShipping={newVal => {
                  props.updateShipping({ ...values, ...newVal });
                }}
              />
            )}

            {props.isTradeUser ? (
              <TwoColumnFormFields>
                <PhoneNumberField
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  error={errors.phone_number}
                  value={values.phone_number}
                  isTouched={touched.phone_number}
                  updateShipping={() => {
                    props.updateShipping(values);
                  }}
                />

                <ShippingEmailField
                  value={values.shipping_email}
                  error={errors.shipping_email}
                  isTouched={touched.shipping_email}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                />
              </TwoColumnFormFields>
            ) : (
              <PhoneNumberField
                handleBlur={handleBlur}
                handleChange={handleChange}
                error={errors.phone_number}
                value={values.phone_number}
                isTouched={touched.phone_number}
                updateShipping={() => {
                  props.updateShipping(values);
                }}
              />
            )}

            <div className="mt-8 bg-white font-bold text-xs md:text-sm text-gray text-center w-full min-w-0">
              <ToggleSwitch
                fullWidthOnSmallScreens
                isChecked={!!values.shipping_sms}
                onClick={() => {
                  const updatedShippingSms = !values.shipping_sms;

                  setFieldValue('shipping_sms', updatedShippingSms);

                  props.updateShipping({
                    ...values,
                    shipping_sms: updatedShippingSms,
                  });
                }}
                size="sm"
              >
                I&apos;d also like to receive shipping updates via text message.
                By signing up, you agree to our{' '}
                <Link
                  className="text-brand cursor-pointer font-bold"
                  to="/privacy-policy/"
                >
                  Privacy Policy.
                </Link>
              </ToggleSwitch>
            </div>

            <div className="flex justify-end my-[30px]">
              <CheckoutSubmitButton
                dataXc="shipping-submit-btn"
                id="continue-to-delivery"
                disabled={
                  props.disableSubmitBtn || (submitCount > 0 && !isValid)
                }
              >
                Continue to delivery
              </CheckoutSubmitButton>
            </div>
          </div>
          <FormikFirstErrorFieldFocusOnSubmit
            formFieldRefs={{
              address: addressRef,
            }}
          />
        </form>
      )}
    />
  );
};

Shipping.propTypes = {
  disableSubmitBtn: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  shipping: PropTypes.object,
  updateShipping: PropTypes.func.isRequired,
  validateFormOnLoad: PropTypes.bool,
  isTradeUser: PropTypes.bool,
};

export default Shipping;
